import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { ContactService } from './contact.service';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { INgxsFormModel } from '@pro-connect/shared';
import { concatMap, Observable, of, throwError, timeout } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { IContactLoggedInFormModel } from './models/contact-logged-in-form-model';
import { IContactNotLoggedInFormModel } from './models/contact-not-logged-in-form-model';
import { IContactNotLoggedInDto } from './models/contact-not-logged-in-dto';
import { IContactLoggedInDto } from './models/contact-logged-in-dto';
import { IContactSubject } from './models/contact-subject';

export class ContactStateSubmitMessageNotLoggedIn {
  public static readonly type = '[ContactState] submit message not logged in';
}

export class ContactStateSubmitMessageLoggedIn {
  public static readonly type = '[ContactState] submit message logged in';
}

export class ContactStateGetContactSubjects {
  public static readonly type = '[ContactState] get contact subjects';
}

export interface ContactStateModel {
  loading: boolean;
  formLoggedIn: INgxsFormModel<IContactLoggedInFormModel>;
  formNotLoggedIn: INgxsFormModel<IContactNotLoggedInFormModel>;
  contactSubjects: IContactSubject[] | null;
}

@State<ContactStateModel>({
  name: 'contact',
  defaults: {
    loading: false,
    formLoggedIn: {
      model: {
        subject: null,
        message: null,
      },
      errors: {},
      dirty: false,
      disabled: false,
      status: 'pristine',
    },
    formNotLoggedIn: {
      model: {
        subject: null,
        message: null,
        email: null,
        lastname: null,
        firstname: null,
        phone: null,
        zipCode: null,
        saloonCode: null,
      },
      errors: {},
      dirty: false,
      disabled: false,
      status: 'pristine',
    },
    contactSubjects: null,
  },
})
@Injectable()
export class ContactState {
  public constructor(
    private readonly _activateAccountService: ContactService,
    private readonly _recaptchaV3Service: ReCaptchaV3Service,
  ) {}

  @Selector()
  public static loading({ loading }: ContactStateModel): boolean {
    return loading;
  }

  @Selector()
  public static contactSubjects({ contactSubjects }: ContactStateModel): IContactSubject[] | null {
    return contactSubjects;
  }

  @Action(ContactStateSubmitMessageNotLoggedIn)
  public submitMessageNotLoggedIn({ getState, patchState }: StateContext<ContactStateModel>): Observable<unknown> {
    const {
      formNotLoggedIn: {
        model: { firstname, lastname, message, email, subject, zipCode, phone, saloonCode },
      },
    } = getState();

    if (!firstname || !lastname || !message || !email || !subject || !zipCode || !phone) {
      return throwError(() => 'Form is invalid');
    }

    const dto: IContactNotLoggedInDto = {
      firstname,
      lastname,
      message,
      email,
      subject,
      zipCode,
      phone,
      saloonCode,
    };

    patchState({ loading: true });
    return this._recaptchaV3Service.execute('importantAction').pipe(
      timeout({ first: 5000, with: () => throwError(() => 'timeout') }),
      concatMap((recaptchaKey: string) =>
        this._activateAccountService.submitContactMessageNotLoggedIn(dto, recaptchaKey),
      ),
      catchError((error) => throwError(() => error)),
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }

  @Action(ContactStateSubmitMessageLoggedIn)
  public submitMessageLoggedIn({ getState, patchState }: StateContext<ContactStateModel>): Observable<unknown> {
    const {
      formLoggedIn: {
        model: { message, subject },
      },
    } = getState();

    if (!message || !subject) {
      return throwError(() => 'Form is invalid');
    }

    const dto: IContactLoggedInDto = {
      message,
      subject,
    };

    patchState({ loading: true });
    return this._activateAccountService.submitContactMessageLoggedIn(dto).pipe(
      catchError((error) => throwError(() => error)),
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }

  @Action(ContactStateGetContactSubjects)
  public getContactSubjects({ getState, patchState }: StateContext<ContactStateModel>): Observable<IContactSubject[]> {
    const { contactSubjects } = getState();
    if (contactSubjects?.length) {
      return of(contactSubjects);
    }
    patchState({ loading: true });
    return this._activateAccountService.getContactSubjects().pipe(
      catchError((error) => throwError(() => error)),
      tap((contactSubjects: IContactSubject[]) => {
        patchState({ contactSubjects });
      }),
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }
}
